import React, { useEffect, useState } from "react";

import { useQuery } from "@apollo/client";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  InputAdornment,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { makeStyles, withStyles } from "tss-react/mui";

import { graphql } from "@/gql";
import { colors } from "@/theme/overrides/colors";
import { maybeCall } from "@/utils/functions";

import SelectedClientView from "./SelectedClientView";
import UserListItem from "./UserListItem";

const useStyles = makeStyles()({
  container: {
    minWidth: 350,
    position: "relative",
  },
  listContainer: {
    position: "absolute",
    width: "100%",
    marginTop: 1,
    boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
    backgroundColor: colors.white,
    zIndex: 9,
  },
  loading: {
    padding: 20,
  },
  loadingText: {
    fontSize: 14,
    textAlign: "center",
  },
  list: {
    display: "flex",
    flexDirection: "column",
    maxHeight: 350,
    overflow: "hidden",
    overflowY: "auto",
  },
  optionContainer: {
    padding: 20,
    display: "flex",
    justifyContent: "flex-end",
    borderTopStyle: "solid",
    borderTopWidth: 1,
    borderTopColor: "#C8CED8",
  },
});

const StyledTextField = withStyles(TextField, {
  root: {
    "& .MuiInputBase-input": {
      display: "flex",
      alignItems: "center",
      height: 52,
      padding: 0,
      paddingLeft: 16,
      paddingRight: 28,
    },
  },
});

const StyledButton = withStyles(Button, (theme) => ({
  contained: {
    height: theme.typography.pxToRem(32),
  },
}));

type SearchClientListViewProps = {
  value?: string;
  useRadio?: boolean;
  onChange: (id: string) => void;
  onBlur?: () => void;
  errorMessage?: string | undefined;
  hasSubmitted: boolean;
};

const searchClientListViewQuery = graphql(`
  query SearchClientListViewQuery {
    clients {
      nodes {
        ...UserListItem
      }
    }
  }
`);

const SearchClientListView: React.FC<SearchClientListViewProps> = ({
  value,
  onChange,
  useRadio = false,
  errorMessage,
  onBlur,
  hasSubmitted,
}) => {
  const { classes } = useStyles();
  const { loading, data } = useQuery(searchClientListViewQuery);
  const clients = data?.clients.nodes;
  const [showList, setShowList] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [allClients, setAllClients] = React.useState<typeof clients>([]);
  const [hasSelected, setHasSelected] = useState(false);
  const [selectedClientID, setSelectedClientID] = useState<string>("");
  const selectorRef = React.useRef<HTMLInputElement>(null);
  const closeList = () => setShowList(false);
  const openList = () => setShowList(true);

  function handleSearch(event: React.ChangeEvent<HTMLInputElement>) {
    const value = event.target.value.toLowerCase();
    if (value !== "" && allClients !== undefined) {
      const newClients = allClients.filter((client) => {
        return client?.profile?.fullName.toLowerCase().includes(value);
      });
      setAllClients(newClients);
    } else {
      setAllClients(clients);
    }
    if (value == "") {
      closeList();
    } else {
      openList();
    }
    setSearchValue(value);
  }

  useEffect(() => {
    setSearchValue("");
    if (value != null && value !== "") {
      setHasSelected(true);
      setSelectedClientID(value);
    } else {
      setHasSelected(false);
      setSelectedClientID("");
    }
  }, [value]);

  function handleSelectedUser(clientId: string | null | undefined) {
    setSelectedClientID(clientId ?? "");
    if (!useRadio) {
      onChange(clientId ?? "");
      setHasSelected(true);
      closeList();
      maybeCall(onBlur);
    }
  }

  function clearSelected() {
    setSearchValue("");
    setSelectedClientID("");
    setHasSelected(false);
  }

  useEffect(() => {
    if (clients) {
      setAllClients(clients);
    }
  }, [clients]);

  useEffect(() => {
    if (hasSubmitted) {
      clearSelected();
    }
  }, [hasSubmitted]);

  useEffect(() => {
    // Click outside listener
    function clickHandler({ target }: MouseEvent) {
      if (
        selectorRef.current &&
        !selectorRef.current.contains(target as Node)
      ) {
        closeList();
      }
    }
    document.addEventListener("mousedown", clickHandler);
    return () => {
      document.removeEventListener("mousedown", clickHandler);
    };
  }, [selectorRef]);

  return (
    <div ref={selectorRef} className={classes.container}>
      {hasSelected ? (
        <SelectedClientView id={selectedClientID} onClose={clearSelected} />
      ) : (
        <StyledTextField
          value={searchValue}
          error={!!errorMessage}
          onChange={handleSearch}
          onBlur={onBlur}
          fullWidth
          placeholder="Search"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon htmlColor={colors.primary} />
              </InputAdornment>
            ),
          }}
        />
      )}
      {showList && (
        <Box className={classes.listContainer}>
          {loading ? (
            <Box className={classes.loading}>
              <Typography className={classes.loadingText}>
                Fetching users. Please wait...
              </Typography>
            </Box>
          ) : (
            <Box>
              <Box className={classes.list}>
                <RadioGroup
                  name="user"
                  onChange={(e) => setSelectedClientID(e.target.value)}
                  defaultValue={selectedClientID}
                >
                  {allClients?.map((data, index) => (
                    <UserListItem
                      key={index}
                      client={data}
                      useRadio={useRadio}
                      onSelect={() => handleSelectedUser(data.id)}
                    />
                  ))}
                </RadioGroup>
              </Box>
              {useRadio && (
                <Box className={classes.optionContainer}>
                  <StyledButton
                    disabled={!allClients?.length}
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      onChange(selectedClientID);
                      setHasSelected(true);
                      closeList();
                      maybeCall(onBlur);
                    }}
                  >
                    Done
                  </StyledButton>
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}
    </div>
  );
};

export default SearchClientListView;
